import { useCallback, useEffect, useRef, useState } from "react";
import type { AuthLoginResponseDto } from "@/api/space/dtos/authenticator.dto";
import useAutenticacaoJwt from "../../../../useAutenticacaoJwt";
import type { IdentificacaoGrupoLogin, ResultadoEntrarUnificado } from "../../../../tiposAutenticacaoJwt";
import type { DesafioOtpLogin, VerificacaoTurnstileLogin } from "../loginUnificado.types";
import type useBloqueioTentativasLogin from "./useBloqueioTentativasLogin";
import { formatarEsperaLogin, obterMensagemErroLogin } from "../utils/loginUnificado.utils";

// --------------------------------- TYPES ---------------------------------
type UseFluxoOtpSpaceUnificadoOptions = {
	readonly identificacao: IdentificacaoGrupoLogin;
	readonly bloqueio: ReturnType<typeof useBloqueioTentativasLogin>;
	readonly verificacao: VerificacaoTurnstileLogin;
	readonly aoIniciarDesafio: (
		nome: string,
		token: string,
		signal: AbortSignal,
	) => Promise<ResultadoEntrarUnificado | null>;
	readonly aoConfirmarOtp: (dados: {
		readonly desafio: string;
		readonly codigo: string;
		readonly token: string;
		readonly signal: AbortSignal;
	}) => Promise<AuthLoginResponseDto>;
	readonly aoFechar: () => void;
	readonly aoExpirar: () => void;
};

// ------------------------------- FUNCTIONS -------------------------------
export default function useFluxoOtpSpaceUnificado({
	identificacao,
	bloqueio,
	verificacao,
	aoIniciarDesafio,
	aoConfirmarOtp,
	aoFechar,
	aoExpirar,
}: Readonly<UseFluxoOtpSpaceUnificadoOptions>) {
	// -------------------------------- STATES --------------------------------
	const [nomeUsuario, setNomeUsuario] = useState<string | null>(null);
	const [desafio, setDesafio] = useState<DesafioOtpLogin | null>(null);
	const [codigoPendente, setCodigoPendente] = useState<string | null>(null);
	const [mensagemErro, setMensagemErro] = useState<string | undefined>(undefined);
	const [enviando, setEnviando] = useState(false);
	const [inicioFalhou, setInicioFalhou] = useState(false);
	const [revisaoCodigo, setRevisaoCodigo] = useState(0);
	const controllerRef = useRef<AbortController | null>(null);
	const montadoRef = useRef(false);
	const nomeRef = useRef<string | null>(null);

	const desafioRef = useRef(desafio);
	desafioRef.current = desafio;

	const { renovar } = verificacao;
	const { concluirEntrarUnificado } = useAutenticacaoJwt();

	// -------------------------------- EFFECTS -------------------------------
	useEffect(() => {
		montadoRef.current = true;
		return () => {
			montadoRef.current = false;
			controllerRef.current?.abort();
		};
	}, []);

	useEffect(() => {
		if (!desafio) return;

		function verificarPrazo() {
			if (Date.now() < desafio.expiraEm) return;
			controllerRef.current?.abort();
			aoExpirar();
		}

		verificarPrazo();

		const timer = globalThis.setInterval(verificarPrazo, 250);

		return () => globalThis.clearInterval(timer);
	}, [desafio, aoExpirar]);

	useEffect(() => {
		if (!nomeUsuario || !verificacao.token || controllerRef.current || bloqueio.estaBloqueado() || inicioFalhou)
			return;
		if (desafio && !codigoPendente) return;

		if (desafio && Date.now() >= desafio.expiraEm) return aoExpirar();

		const token = verificacao.consumirToken();
		if (!token) return;

		const controller = new AbortController();
		controllerRef.current = controller;
		setCodigoPendente(null);
		setEnviando(true);
		setMensagemErro(undefined);

		const tentativaAtual = () => !controller.signal.aborted && montadoRef.current;

		async function iniciarDesafio(): Promise<void> {
			const resultado = await aoIniciarDesafio(nomeUsuario, token, controller.signal);

			if (!tentativaAtual()) return;
			if (resultado?.status !== 202) throw new Error("Não foi possível iniciar o desafio OTP.");

			setDesafio({
				valor: resultado.dados.desafio_otp,
				expiraEm: Date.now() + resultado.dados.expiracao_segundos * 1000,
				identificacao,
				versaoSessao: resultado.versaoSessao,
			});
		}

		async function confirmarOtp(desafioAtual: DesafioOtpLogin): Promise<void> {
			if (!codigoPendente) return;
			const resposta = await aoConfirmarOtp({
				desafio: desafioAtual.valor,
				codigo: codigoPendente,
				token,
				signal: controller.signal,
			});

			if (!tentativaAtual()) return;
			if (Date.now() >= desafioAtual.expiraEm) return aoExpirar();

			const concluido = concluirEntrarUnificado?.({
				resposta,
				identificacao,
				signal: controller.signal,
				versaoSessao: desafioAtual.versaoSessao,
			});

			if (concluido) aoFechar();
		}

		async function executarTentativa(): Promise<void> {
			try {
				if (desafio) await confirmarOtp(desafio);
				else await iniciarDesafio();
			} catch (error: unknown) {
				if (!tentativaAtual()) return;
				bloqueio.registrarErro(error);
				setMensagemErro(obterMensagemErroLogin(error, desafio ? "otp" : "identificado"));
				setRevisaoCodigo((atual) => atual + 1);
				if (!desafio) setInicioFalhou(true);
				renovar();
			} finally {
				if (controllerRef.current === controller) {
					controllerRef.current = null;
					if (tentativaAtual()) setEnviando(false);
				}
			}
		}
		void executarTentativa();
	}, [
		nomeUsuario,
		inicioFalhou,
		codigoPendente,
		desafio,
		verificacao,
		bloqueio,
		renovar,
		aoIniciarDesafio,
		aoConfirmarOtp,
		concluirEntrarUnificado,
		identificacao,
		aoFechar,
		aoExpirar,
	]);

	// ----------------------------- DERIVED STATE ----------------------------
	const erroExibido =
		bloqueio.segundosRestantes > 0
			? `Muitas tentativas sem sucesso. Aguarde ${formatarEsperaLogin(bloqueio.segundosRestantes)} para tentar de novo.`
			: mensagemErro;

	// -------------------------------- HANDLERS ------------------------------
	const validarNome = useCallback(
		(nome: string | null): void => {
			if (nomeRef.current === nome) return;

			nomeRef.current = nome;
			const abortouInicio = controllerRef.current !== null && desafioRef.current === null;
			controllerRef.current?.abort();
			controllerRef.current = null;
			setNomeUsuario(nome);
			setDesafio(null);
			setCodigoPendente(null);
			setMensagemErro(undefined);
			setInicioFalhou(false);
			setEnviando(false);

			if (abortouInicio) renovar();
		},
		[renovar],
	);

	function verificar(codigo: string): void {
		if (
			controllerRef.current ||
			bloqueio.estaBloqueado() ||
			!nomeRef.current ||
			!desafio ||
			!verificacao.token ||
			!/^\d{6}$/.test(codigo)
		)
			return;
		if (desafio && Date.now() >= desafio.expiraEm) return aoExpirar();
		setCodigoPendente(codigo);
	}

	function alterarCodigo(codigo: string): void {
		if (codigo.length < 6) setCodigoPendente(null);
	}

	function tentarNovamente(): void {
		if (controllerRef.current || bloqueio.estaBloqueado() || !inicioFalhou) return;
		setMensagemErro(undefined);
		setInicioFalhou(false);
	}

	// -------------------------------- RETURN --------------------------------
	return {
		nomeUsuario,
		desafio,
		enviando,
		inicioFalhou,
		revisaoCodigo,
		erroExibido,
		validarNome,
		verificar,
		alterarCodigo,
		tentarNovamente,
	};
}
